/*
 * Cyphon login popup (sections/login-popup.liquid, shown in the theme dialog by assets/section-popup.js).
 * - opening animation: GSAP when it is available (it is fetched on the first hover / touch / focus of the account
 *   icon, so it never competes with page load); a CSS keyframe fallback otherwise
 * - tab switch (login / create account / forgot password) gets the same soft reveal
 * - show / hide password button (event delegation, the popup content is inserted after load)
 * No scroll handlers, no polling; the observer watches only direct children of <body>.
 */
(() => {
  if (window.__cyLp) return; // the account snippet can be rendered more than once (desktop and mobile header)
  window.__cyLp = 1;
  const gsapUrl = document.currentScript && document.currentScript.dataset.gsap;
  const reduce = matchMedia('(prefers-reduced-motion:reduce)').matches;
  let loading;
  const load = () => {
    if (window.gsap || !gsapUrl) return Promise.resolve();
    return (loading = loading || new Promise((ok, no) => {
      const el = document.createElement('script');
      el.src = gsapUrl;
      el.onload = ok;
      el.onerror = no;
      document.head.append(el);
    }));
  };

  // warm GSAP up when the visitor is about to click the account icon
  ['pointerenter', 'focusin', 'touchstart'].forEach((t) =>
    document.addEventListener(
      t,
      (e) => {
        if (!reduce && e.target.closest && e.target.closest('.action-login')) load().catch(() => {});
      },
      { passive: true, capture: true }
    )
  );

  const reveal = (dlg, parts) => {
    const { gsap } = window;
    if (gsap) {
      gsap.from(dlg, { opacity: 0, duration: 0.25, ease: 'power1.out', clearProps: 'opacity' });
      gsap.from(dlg.querySelector('.dlg-box'), { opacity: 0, y: 18, scale: 0.96, duration: 0.45, ease: 'power3.out', clearProps: 'opacity,transform' });
      gsap.from(parts, { opacity: 0, y: 10, duration: 0.4, ease: 'power2.out', stagger: 0.05, delay: 0.12, clearProps: 'opacity,transform' });
    } else {
      dlg.classList.add('cy-lp-css');
      load().catch(() => {});
    }
  };
  // hover fill (bottom to top) on the social tiles and the submit button: GSAP tweens --f, the CSS scales a layer
  const fillSel = '.cy-lp .h_buttons>div, .cy-lp__submit';
  const fill = (e, to) => {
    if (reduce || e.pointerType !== 'mouse' || !window.gsap) return;
    const el = e.target.closest && e.target.closest(fillSel);
    if (!el || (e.relatedTarget && el.contains(e.relatedTarget))) return;
    window.gsap.to(el, { '--f': to, duration: to ? 0.45 : 0.35, ease: 'power3.out', overwrite: 'auto' });
  };
  document.addEventListener('pointerover', (e) => fill(e, 1), { passive: true });
  document.addEventListener('pointerout', (e) => fill(e, 0), { passive: true });
  const panelParts = (root) => [...root.querySelectorAll('.cy-lp[aria-hidden="false"] > *')];

  new MutationObserver((list) => {
    if (reduce) return;
    for (const m of list) {
      for (const n of m.addedNodes) {
        if (n.nodeType === 1 && /^loginp/.test(n.id)) reveal(n, panelParts(n));
      }
    }
  }).observe(document.body, { childList: true });

  document.addEventListener('click', (e) => {
    const eye = e.target.closest && e.target.closest('[data-cy-eye]');
    if (eye) {
      const input = eye.parentElement.querySelector('input');
      const show = input.type === 'password';
      input.type = show ? 'text' : 'password';
      eye.setAttribute('aria-pressed', show);
      eye.setAttribute('aria-label', show ? eye.dataset.hide : eye.dataset.show);
      return;
    }
    // tab switch: the theme script flips aria-hidden in its own click handler, so look after it ran
    if (!reduce && window.gsap && e.target.closest && e.target.closest('[data-login-show]')) {
      requestAnimationFrame(() => {
        const dlg = document.querySelector('[id^=loginp]');
        if (dlg) window.gsap.from(panelParts(dlg), { opacity: 0, y: 10, duration: 0.35, ease: 'power2.out', stagger: 0.04, clearProps: 'opacity,transform' });
      });
    }
  });
})();
